import { useQuery, useQueryClient } from 'react-query'
import { useState, useEffect } from 'react'
import { 
  fetchLunaticsMentions, 
  fetchTrendingHashtags,
  checkRateLimit,
  isUsingRealData
} from '../services/twitterApi'

// Custom hook for Twitter mentions
export const useTwitterMentions = (limit = 20) => {
  return useQuery({
    queryKey: ['twitter-mentions', limit],
    queryFn: () => fetchLunaticsMentions(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes to respect rate limits
    retry: 1,
    refetchOnWindowFocus: false,
  })
}

// Custom hook for trending hashtags
export const useTrendingHashtags = () => {
  return useQuery({
    queryKey: ['trending-hashtags'],
    queryFn: fetchTrendingHashtags,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // Refresh every 10 minutes to respect rate limits
    retry: 1,
  })
}

// Custom hook for managing Twitter data refresh
export const useTwitterDataManager = () => {
  const queryClient = useQueryClient()
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const refreshData = async () => {
    setIsLoading(true)
    try {
      await queryClient.invalidateQueries({ queryKey: ['twitter-mentions'] })
      await queryClient.invalidateQueries({ queryKey: ['trending-hashtags'] })
      setLastUpdate(new Date())
    } catch (error) {
      console.error('Error refreshing Twitter data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setLastUpdate(new Date())
  }, [])

  return {
    refreshData,
    isLoading,
    lastUpdate
  }
}

// Custom hook for Twitter connection status
export const useTwitterConnectionStatus = () => {
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const usingRealData = isUsingRealData()
        const hasRateLimit = checkRateLimit()
        
        if (usingRealData && hasRateLimit) {
          setIsConnected(true)
          setConnectionError(null)
        } else {
          setIsConnected(false)
          setConnectionError(
            usingRealData 
              ? 'Rate limit reached. Using cached data.' 
              : 'Twitter API not configured. Add your Bearer Token to .env.local'
          )
        }
      } catch (error) {
        setIsConnected(false)
        setConnectionError('Twitter API connection failed')
      }
    }

    checkConnection()
    const interval = setInterval(checkConnection, 60000) // Check every minute

    return () => clearInterval(interval)
  }, [])

  return {
    isConnected,
    connectionError
  }
}

// Custom hook for filtering tweets
export const useFilteredTweets = (tweets: any[], searchTerm = '', hashtag = '') => {
  const filteredTweets = tweets.filter((tweet: any) => {
    const matchesSearch = !searchTerm || 
      tweet.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tweet.username.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesHashtag = !hashtag || 
      tweet.hashtags.some((tag: string) => 
        tag.toLowerCase().includes(hashtag.toLowerCase())
      )
    
    return matchesSearch && matchesHashtag
  })

  return filteredTweets
} 